
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import ShittyTextInput from "@/components/Reusable/CustomInputs/ShittyTextInput.vue";
import {
  AvailableDependencyFilesV,
  ElementDependencyAndCondition
} from "@/types/elements";
import defensiveCopy from "@/util/defensive-copy";
import { ElementStudioContext } from "@/components/ElementStudio/ElementStudioTabsAndPanels.vue";
import Accordion from "@/components/UI/Accordion.vue";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import lodash from "lodash";
import TextWithChevron from "@/components/ElementStudio/TextWithChevron.vue";
import { addOrRerunInlineScriptInHead } from "@/lib/canvas/assets/js-in-head";
import { configStore } from "@/store/store-accessor";
import { VuetifyShapeForSelect } from "@/types/conditions";
import { pipe } from "fp-ts/lib/function";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import * as t from "io-ts";
import { TAGS_AVAILABLE } from "@/types/tags";
import { getBuilderCanvasDocument } from "@/lib/canvas/helpers";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";

type DependencyKeys = keyof ElementDependencyAndCondition;

@Component({
  components: {
    TextInput,
    TextWithChevron,
    CodeInput,
    Accordion,
    ShittyTextInput,
    Repeater
  }
})
export default class ElementStudioDependencies extends Vue {
  @Prop()
  value!: ElementDependencyAndCondition[] | false;

  accordionState: DependencyKeys | "" = "";
  index = 0;
  conditionInfoOpen = false;

  @Inject("context") readonly context!: ElementStudioContext;

  get tags() {
    return TAGS_AVAILABLE;
  }

  get twigVariables() {
    return this.context.getPathsToAllControls();
  }

  async mounted() {
    if (configStore.localDependenciesFiles.length) return;

    await this.getLocalDependenciesFilesFromWordpress();
  }

  async getLocalDependenciesFilesFromWordpress() {
    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_get_local_dependency_files",
        decoder: t.array(AvailableDependencyFilesV),
        resultKey: "localDependencyFiles"
      }),
      fold(
        errors => errors.forEach(e => NotificationLogger.log.message(e.msg)),
        localDependencyFiles => {
          configStore.setLocalDependenciesFiles(localDependencyFiles);
        }
      )
    );
  }

  updateDependenciesRoot(newValue: ElementDependencyAndCondition[]) {
    this.context.updateDependency(newValue);
  }

  updateDependencies(newValue: ({} | string)[], path: string) {
    const newDependencies = defensiveCopy(this.value) || [];

    lodash.set(
      newDependencies,
      path,
      // Turn objects into string. It happens when Repeater adds a new item
      newValue.map(dependency =>
        typeof dependency === "string" ? dependency : ""
      )
    );

    this.context.updateDependency(newDependencies);
  }

  updateDependency(newValue: string, path: string) {
    const newDependencies = defensiveCopy(this.value) || [];
    lodash.set(newDependencies, path, newValue);
    this.context.updateDependency(newDependencies);
  }

  toggleAccordion(state: DependencyKeys) {
    this.accordionState = this.accordionState === state ? "" : state;
  }

  executeInlineScript(path: string) {
    const inlineScript = lodash.get(this.value, path);

    if (inlineScript) {
      addOrRerunInlineScriptInHead(getBuilderCanvasDocument(), {
        code: inlineScript,
        origin: `Executed inline script from Element Studio`
      });
    }
  }

  dependencyIsNotLocal(value: string, localDepsItems: VuetifyShapeForSelect[]) {
    return !localDepsItems.find(
      localDep => "value" in localDep && localDep.value === value
    );
  }

  get jsLocalDependencies() {
    return this.localDependenciesAsVuetifySelectShape("js");
  }

  get cssLocalDependencies() {
    return this.localDependenciesAsVuetifySelectShape("css");
  }

  localDependenciesAsVuetifySelectShape(fileType: "js" | "css") {
    const localDependencies: VuetifyShapeForSelect[] = [];

    configStore.localDependenciesFiles.forEach(dependencyFile => {
      if (!dependencyFile.files[fileType].length) return;

      localDependencies.push({ header: dependencyFile.name });

      dependencyFile.files[fileType].forEach(file => {
        localDependencies.push({
          text: file.name,
          value: file.url
        });
      });
    });

    return localDependencies;
  }
}
